import { readValueByName } from '../field-paths';
import { getDefaultPlaceholder } from '../field-placeholders';
import {
  BASE_TYPE_CONFIG,
  applyIncludeExcludeFilter,
  getOptionsConfigForSourceType,
  hasFormBasedOptions,
  hasSourceType,
  resolveOptions,
  wrapMultipleChoiceOptions,
} from '../options-resolver';
import { processTemplates } from '../template-processor';
import { camelCaseObjectKeys } from '../text';

const SKIP_CAMEL_KEYS = ['code', 'options', 'metadata', 'option_sets'];

// ============================================================================
// Type Mapping (uses BASE_TYPE_CONFIG from options-resolver)
// ============================================================================

export const mapFieldType = type => {
  if (!type) return type;
  const isMulti = type.startsWith('multi_');
  const baseType = isMulti ? type.substring(6) : type;
  if (BASE_TYPE_CONFIG[baseType]) return isMulti ? 'multi_select' : 'select';
  return type;
};

export { hasSourceType } from '../options-resolver';

// ============================================================================
// Field Processing (Main Entry Points)
// ============================================================================

// Process a single field: camelCase keys and resolve static options from source
// Recursively processes subFields (matching candidate fields pattern)
// Static source options (countries, adminDivisions) are resolved here
// Dynamic options (form field references) are resolved reactively in the component
//
// Processing order (must match dynamic processing in FieldConfigRenderer):
// 1. CamelCase field keys (see SKIP_CAMEL_KEYS)
// 2. Resolve static options for field (countries, adminDivisions, etc.)
// 3. Recursively process subFields (same order: camelCase -> resolve static options)
const processField = (field, templateContext = null) => {
  if (!field || typeof field !== 'object') {
    return field;
  }

  // Step 1: CamelCase field keys
  const camelField = camelCaseObjectKeys(field, SKIP_CAMEL_KEYS);

  // Step 2: Resolve static options from source (countries, adminDivisions, etc.)
  // Resolve for all source-type fields regardless of field-level conditions (conditions only affect visibility).
  // Only resolve if options is a source object (not already resolved array or form field reference)
  const originalType = camelField.type;
  if (originalType) {
    // Check if options is a static source (not a form field reference or already resolved)
    const isStaticSource =
      camelField.options &&
      typeof camelField.options === 'object' &&
      !Array.isArray(camelField.options) &&
      camelField.options.source &&
      !hasFormBasedOptions(camelField.options);

    // Also check if it's a type that has a fallback source in BASE_TYPE_CONFIG
    const hasFallbackSource = hasSourceType(originalType);

    // If type has a fallback source and options is missing/undefined, resolve it
    // Or if options exists and is a static source or needs fallback resolution
    if (
      (hasFallbackSource && !camelField.options) ||
      isStaticSource ||
      (hasFallbackSource && camelField.options && !camelField.options.source)
    ) {
      // Always use getOptionsConfigForSourceType so field-level exclude/include are applied
      // for every source-type field (e.g. both countries_to_check and must_include_countries).
      const optionsConfig = getOptionsConfigForSourceType(camelField, camelField.options);
      const resolvedOptions = resolveOptions(originalType, optionsConfig, templateContext?.sources);
      if (resolvedOptions && Array.isArray(resolvedOptions)) {
        camelField.options = resolvedOptions;
        // Strip exclude/include so downstream (toQuestion, dynamic) doesn't reprocess
        delete camelField.exclude;
        delete camelField.include;
      }
    }
  }

  // Step 3: Recursively process subFields (same order: camelCase -> resolve static options)
  if (Array.isArray(camelField.subFields)) {
    camelField.subFields = camelField.subFields.map(sf => processField(sf, templateContext));
  }

  return camelField;
};

// Process fields array: camelCase all field keys and resolve static options (matching candidate fields pattern)
// This should be called before passing fields to FieldConfigRenderer
// Static source options (countries, adminDivisions) are resolved here
// Dynamic options (form field references) are resolved reactively in the component
export const processFields = (fields, templateContext = null) => {
  if (!Array.isArray(fields)) {
    return [];
  }
  return fields.map(field => processField(field, templateContext));
};

// Transform a field config (configuration, client_fields, extra_result_details) to QuestionWrapper format
export const toQuestion = (field, resolveSubFields = true, templateContext = null) => {
  if (!field || typeof field !== 'object') {
    return field;
  }

  const processedField = camelCaseObjectKeys(field, SKIP_CAMEL_KEYS);

  // Process templates in text fields if context is provided (single round: replace unresolved with '')
  const processText = text => {
    if (!templateContext?.sources || !text) return text;
    return processTemplates(text, templateContext.sources, false, true);
  };

  // Normalize type (e.g., country -> select, multi_country -> multi_select)
  const originalType = processedField.type;
  processedField.type = mapFieldType(originalType);

  // Set id = code if not already set
  if (!processedField.id) {
    processedField.id = processedField.code;
  }

  // Process text fields with templates (fields already have correct keys: title, description, helpText, text)
  if (processedField.title) processedField.title = processText(processedField.title);
  if (processedField.description)
    processedField.description = processText(processedField.description);
  if (processedField.helpText) processedField.helpText = processText(processedField.helpText);
  if (processedField.text) processedField.text = processText(processedField.text);

  // Resolve placeholder - use field's placeholder or fall back to type default
  if (!processedField.placeholder) {
    processedField.placeholder = getDefaultPlaceholder(processedField.type, processedField.title);
  }
  if (processedField.placeholder) {
    processedField.placeholder = processText(processedField.placeholder);
  }

  // Handle options
  // First, resolve options for source types if missing (e.g., multi_country should auto-populate countries)
  // Options are resolved once for all fields (including conditionally visible) in processFields
  if (hasSourceType(originalType) && !processedField.options) {
    const optionsConfig = getOptionsConfigForSourceType(processedField);
    const resolvedOptions = resolveOptions(originalType, optionsConfig);
    if (resolvedOptions && Array.isArray(resolvedOptions)) {
      processedField.options = resolvedOptions;
    }
  }

  if (processedField.options) {
    // field_resolver_service returns { options: [...], dynamic_dependency_codes: [...] } when options have dynamic deps
    if (
      typeof processedField.options === 'object' &&
      !Array.isArray(processedField.options) &&
      Array.isArray(processedField.options.options)
    ) {
      processedField.options = processedField.options.options;
    }

    // Safety check: if options is still an unresolved source object, default to empty array
    // Form field references (direct field codes) are handled dynamically, so skip those
    if (
      typeof processedField.options === 'object' &&
      !Array.isArray(processedField.options) &&
      processedField.options.source &&
      !hasFormBasedOptions(processedField.options)
    ) {
      processedField.options = [];
    }

    // Wrap options for multiple_choice type
    if (processedField.type === 'multiple_choice') {
      processedField.options = wrapMultipleChoiceOptions(processedField.options, processedField);
    }

    // Ensure select and multi_select always have array options
    if (processedField.type === 'select' || processedField.type === 'multi_select') {
      if (!Array.isArray(processedField.options)) {
        // If it's an object with options array (from multiple_choice normalization), extract it
        if (processedField.options?.options && Array.isArray(processedField.options.options)) {
          processedField.options = processedField.options.options;
        } else {
          // Otherwise default to empty array
          processedField.options = [];
        }
      }
    }

    // Apply field-level exclude/include for source types after options are normalized to an array.
    // Runs for all source-type fields (including conditionally visible e.g. must_include_countries)
    // and for both static and backend { options, dynamic_dependency_codes } format.
    if (
      hasSourceType(originalType) &&
      Array.isArray(processedField.options) &&
      processedField.options.length > 0
    ) {
      const { exclude: excl, include: incl } = getOptionsConfigForSourceType(processedField);
      if (excl.length > 0 || incl.length > 0) {
        processedField.options = applyIncludeExcludeFilter(processedField.options, incl, excl);
      }
    }
  }

  // Set useSearch for source types
  if (hasSourceType(originalType) && processedField.useSearch === undefined) {
    processedField.useSearch = true;
  }

  // Ensure required is boolean
  if (processedField.required === undefined) {
    processedField.required = false;
  }

  if (processedField.type === 'phone_number' && !processedField.defaultCountry) {
    const companyCountry = templateContext?.sources?.company?.country;
    if (companyCountry) {
      processedField.defaultCountry = companyCountry;
    }
  }

  if (templateContext?.sources) {
    const resolvePathValue = path => {
      if (typeof path !== 'string' || !path.includes('.')) return undefined;
      const first = path.split('.')[0];
      if (!Object.prototype.hasOwnProperty.call(templateContext.sources, first)) return undefined;
      return readValueByName(templateContext.sources, path);
    };

    const rawRequired = processedField.required;
    if (typeof rawRequired === 'string' && rawRequired.includes('.')) {
      const resolvedRequired = resolvePathValue(rawRequired);
      processedField.required =
        resolvedRequired === true || resolvedRequired === 'true' || resolvedRequired === 1;
    }

    if (processedField.validationParams && typeof processedField.validationParams === 'object') {
      const resolvedParams = { ...processedField.validationParams };
      for (const [key, val] of Object.entries(resolvedParams)) {
        if (typeof val !== 'string' || !val.includes('.')) continue;
        let resolved = resolvePathValue(val);
        if (
          Array.isArray(resolved) &&
          resolved.length > 0 &&
          typeof resolved[0] === 'object' &&
          resolved[0]?.value != null
        ) {
          resolved = resolved.map(item => item.value);
        }
        if (resolved !== undefined) {
          resolvedParams[key] = resolved;
        }
      }
      processedField.validationParams = resolvedParams;
    }
  }

  if (processedField.max != null && typeof processedField.max === 'string') {
    const resolvedMax = processText(processedField.max);
    const n = Number(resolvedMax);
    if (resolvedMax !== '' && Number.isFinite(n)) {
      processedField.max = n;
    } else {
      delete processedField.max;
    }
  }

  // Build sectionHeader if sectionTitle is present (with template processing)
  if (processedField.sectionTitle || processedField.sectionHelpText) {
    processedField.sectionHeader = {
      title: processText(processedField.sectionTitle),
      helpText: processText(processedField.sectionHelpText),
    };
  }

  // Recursively process subFields
  if (resolveSubFields && Array.isArray(processedField.subFields)) {
    processedField.subFields = processedField.subFields.map(sf =>
      toQuestion(sf, false, templateContext),
    );
  }

  if (templateContext?.sources) {
    const rawDef = processedField.defaultValue;
    if (rawDef != null && typeof rawDef === 'string' && rawDef.includes('.')) {
      const first = rawDef.split('.')[0];
      if (Object.prototype.hasOwnProperty.call(templateContext.sources, first)) {
        const resolved = readValueByName(templateContext.sources, rawDef);
        if (resolved !== undefined) {
          processedField.defaultValue = resolved;
        } else {
          delete processedField.defaultValue;
        }
      }
    }
  }

  return processedField;
};

export const toQuestions = fields => {
  if (!Array.isArray(fields)) {
    return [];
  }
  return fields.map(toQuestion);
};

// Re-export options helpers for consumers that import from process-static-content
export {
  getOptionCountBehavior,
  hasFormBasedOptions,
  resolveOptionsWithFormValues,
} from '../options-resolver';

// ============================================================================
// Utility Helpers
// ============================================================================

// Helper to build field name with prefix (used in component)
export const getFieldName = (code, prefix) => {
  if (!prefix) return code;
  return `${prefix}.${code}`;
};
